import { DataSource } from '@angular/cdk/table'
import { ErrorHandler, Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'

import { BehaviorSubject, Observable, combineLatest, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { LaskunAsiakas } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'

interface Hakutiedot {
  vapaahaku: string
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

/**
 * Read-only datasource lemonator-puolen laskunasiakkaiden selaukselle. Kysyy customers/{asiakasAvain}/lasku-asiakkaat
 * Lemonaid-firestoresta ja suodattaa client-puolella nimen, y-tunnuksen, ulkoisen tunnisteen tai
 * sähköpostiosoitteen mukaan.
 */
@Injectable()
export class LaskuAsiakkaatSelaaDataSourceService extends DataSource<LaskunAsiakas> {

  private _hakutiedot: Hakutiedot = {
    vapaahaku: '',
    sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
    lajittelutiedot: { active: 'nimi', direction: 'asc' },
  }
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)
  hakutiedotObservable: Observable<Hakutiedot> = this._hakutiedotSubject.asObservable()

  private _lataaSubject = new BehaviorSubject<boolean>(true)
  lataaObservable: Observable<boolean> = this._lataaSubject.asObservable()

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  private _kaikkiAsiakkaatObservable: Observable<LaskunAsiakas[]>
  nakyvatAsiakkaatObservable: Observable<LaskunAsiakas[]>

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _asiakasService: AsiakasService,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this._kaikkiAsiakkaatObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      tap(() => this._lataaSubject.next(true)),
      switchMap(avainTiedot => {
        if (!avainTiedot?.avain) {
          return observableOf<LaskunAsiakas[]>([])
        }
        const uri = this._laskuUriService.getLaskuAsiakkaatCollectionUri(avainTiedot.avain)
        return this._firebaseLemonaid.firestoreCollection<LaskunAsiakas>(uri).listenSnapshots().pipe(
          map(snapshots => snapshots.map(snap => {
            const a = snap.data()
            a.avain = snap.id
            return a
          })),
        )
      }),
      tap(() => this._lataaSubject.next(false)),
      lemonShare(),
    )

    this.nakyvatAsiakkaatObservable = combineLatest([this._kaikkiAsiakkaatObservable, this._hakutiedotSubject]).pipe(
      map(([asiakkaat, hakutiedot]) => {
        let suodatetut = asiakkaat.filter(a => !a.poistettu)

        const hakuteksti = hakutiedot.vapaahaku?.trim().toLowerCase()
        if (hakuteksti) {
          suodatetut = suodatetut.filter(a => {
            const nimi = a.nimi?.toLowerCase() ?? ''
            const ytunnus = a.ytunnus?.toLowerCase() ?? ''
            const koodi = a.ulkoinenTunniste?.toLowerCase() ?? ''
            const sposti = (a.laskunVastaanottajat ?? []).join(' ').toLowerCase()
            return nimi.includes(hakuteksti) || ytunnus.includes(hakuteksti) || koodi.includes(hakuteksti) || sposti.includes(hakuteksti)
          })
        }

        const { active, direction } = hakutiedot.lajittelutiedot
        if (active && direction) {
          const suunta = direction === 'asc' ? 1 : -1
          suodatetut = suodatetut.slice().sort((a, b) => this._vertaaKentat(a, b, active) * suunta)
        }

        this._rivienMaaraSubject.next(suodatetut.length)

        const { pageIndex, pageSize } = hakutiedot.sivutustiedot
        const alku = pageIndex * pageSize
        return suodatetut.slice(alku, alku + pageSize)
      }),
      lemonShare(),
    )
  }

  private _vertaaKentat(a: LaskunAsiakas, b: LaskunAsiakas, kentta: string): number {
    const arvoA = String((a as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    const arvoB = String((b as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    return arvoA.localeCompare(arvoB)
  }

  setSearch(vapaahaku: string) {
    this._hakutiedot = { ...this._hakutiedot, vapaahaku, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setPage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  get sort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  connect(): Observable<LaskunAsiakas[]> {
    return this.nakyvatAsiakkaatObservable
  }

  disconnect() {}
}
